import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import moment from "moment";
import { PhoneInput } from "../../components/PhoneInput";
import { IOrder } from "../../interfaces/IOrder";
import { getPickupAddress } from "../../services/addressService";
import { finishOnlineOrder } from "../../services/orderService";
import { getCompletedOrder } from "../../services/orderService";
import { useOrders } from "../../contexts/OrdersContext";
import { Loader } from '../../components/Loader';
import { WelcomeBackModal } from "../../components/WelcomeBackModal";
import { TooltipModal } from "../../components/Tooltip";
import { RememberCardModal } from "../../components/RememberCardModal";
import { formatDescription } from "../../utils";
import { ErrorAlert } from "../../components/ErrorAlert";
import { convertMoney } from "../../utils";
import { CookieConsent } from "../../components/CookieConsent";
import {
    FormField,
    Label,
    Input,
    Select,
    Textarea,
    InlineFormField,
    CheckboxContainer,
    Checkbox,
    ErrorMessage,
    PrimaryButton,
    FormFieldsContainer
} from "../../styles/global";
import { TYPES_OF_DELIVERY, STATES } from "../../constants";
import { rawTelephone } from "../../utils";
import { createClientOnline } from "../../services/clientService";

import { Form, Container, FormHeader, CompletedOrder, OrderReview } from "./style";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons";

interface INewOrder {
    first_name: string;
    last_name: string;
    phone_number: string;
    country_code: string;
    addressId: string;
    pickup_on_store: boolean;
    type_of_delivery: string;
    postal_code: string;
    city: string;
    state: string;
    street: string;
    street_number: string;
    complement: string;
    reference_point: string;
    neighborhood: string;
    country: string;
    receiver_name: string;
    receiver_phone: string;
    receiver_country_code: string;
    delivery_date: string;
    has_card: boolean;
    card_message: string;
    card_from: string;
    card_to: string;
}

export function CompleteOrder() {
    const { addOrder } = useOrders();
    const [storeLogo, setStoreLogo] = useState("");
    const [storeName, setStoreName] = useState("");
    const [storeCNPJ, setStoreCNPJ] = useState("");
    const [storePhone, setStorePhone] = useState("");
    const [showLoader, setShowLoader] = useState(true);
    const [addresses, setAddresses] = useState([]);
    const [client_id, setClientId] = useState("");
    const [addressId, setAddressId] = useState("");
    const [selectedAddress, setSelectedAddress] = useState<any>({});
    const [pickupOnStore, setPickupOnStore] = useState(false);
    const [newAddress, setNewAddress] = useState(false);
    const [isWaitingForClienteOrder, setIsWaitingForClienteOrder] = useState(false);
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const [showRememberCardModal, setShowRememberCardModal] = useState(false);
    const [cardModalShowed, setCardModalShowed] = useState(false);
    const [showToolTipModal, setShowToolTipModal] = useState(false);
    const [maxDeliveryDaysAdvance, setMaxDeliveryDaysAdvance] = useState<number>(30);
    const cardSectionRef = useRef<HTMLDivElement>(null);
    const hasShownWelcomeModal = useRef(false);
    const tooltipMessage = `Para entregas em outras regiões,
        por favor entre em contato conosco pelo whatsapp.`

    const [errorMessage, setErrorMessage] = useState("");
    const [currentOrder, setCurrentOrder] = useState<IOrder | null>(null);
    const discount = currentOrder?.discount || 0;

    const url = window.location.href;
    const urlParts = url.split('/');
    const orderId = urlParts[urlParts.length - 1];

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        setError,
        control,
        formState: { errors, submitCount },
    } = useForm<INewOrder>({
        defaultValues: {
            country: "Brasil",
            state: "RJ",
            city: "Itaperuna",
            postal_code: "28300-000",
            country_code: "BR",
            receiver_country_code: "BR",
        }
    });

    const submitOrder = async (data: INewOrder) => {
        if (!data.has_card && data.type_of_delivery.includes("GIFT") && !cardModalShowed) {
            setCardModalShowed(true);
            setShowRememberCardModal(true);
            return;
        }
        setShowLoader(true);
        const orderData = {
            id: orderId,
            client_id: client_id,
            phone_number: rawTelephone(phone_number),
            country_code: data.country_code,
            first_name: data.first_name,
            last_name: data.last_name,
            pickup_on_store: data.pickup_on_store,
            address_id: addressId,
            type_of_delivery: data.type_of_delivery,
            clientAddress: {
                id: data.addressId,
                phone_number: data.phone_number,
                pickup_on_store: data.pickup_on_store,
                postal_code: data.postal_code,
                city: data.city,
                state: data.state,
                street: data.street,
                street_number: data.street_number,
                complement: data.complement,
                reference_point: data.reference_point,
                neighborhood: data.neighborhood,
                country: data.country,
            },
            receiver_name: data.receiver_name,
            receiver_phone: data.receiver_phone ? rawTelephone(data.receiver_phone) : data.receiver_phone,
            receiver_country_code: data.receiver_country_code,
            delivery_date: data.delivery_date,
            has_card: data.has_card,
            card_message: data.card_message,
            card_from: data.card_from,
            card_to: data.card_to,
            status: 'OPENED',
            editAddress: true,
        }

        try {
            const { data: response } = await finishOnlineOrder(orderData);
            addOrder(response);
            if (response.order.id) {
                setIsWaitingForClienteOrder(false);
            }
            setErrorMessage('');
        }
        catch (error: any) {
            const response = error.response.data
            if (response.code === 401 && response.message === "Invalid order code") {
                setErrorMessage('Código de pedido inválido');
                setTimeout(() => {
                    setErrorMessage('');
                }, 2000);
            }
        }

        setShowLoader(false);
    }

    const phone_number = watch("phone_number");
    const typeOfDelivery = watch("type_of_delivery");
    const hasCard = watch("has_card");

    useEffect(() => {
        const fetchOrderData = async () => {
            const { data: order } = await getCompletedOrder(orderId);
            if (order.status === "WAITING_FOR_CLIENT") {
                setIsWaitingForClienteOrder(true);
                setCurrentOrder(order);
                setStoreLogo(order.store.logo || "");
                setStoreName(order.store.name || "");
                setStoreCNPJ(order.store.cnpj || "");
                setStorePhone(order.store.phone_number || "");
                
                setMaxDeliveryDaysAdvance(order.store.max_delivery_days_advance || 30);
                
                if (order.client) {
                    setValue("phone_number", order.client.phone_number);
                    setValue("country_code", order.client.country_code || "BR");
                    setValue("first_name", order.client.first_name);
                    setValue("last_name", order.client.last_name);
                    setClientId(order.client.id);

                    // Preencher endereços se existirem
                    if (order.client.addresses && order.client.addresses.length > 0) {
                        setAddresses(order.client.addresses);
                    }

                    // Exibir modal de boas-vindas
                    setShowWelcomeModal(true);
                    hasShownWelcomeModal.current = true;
                } else {
                    setValue('phone_number', order.receiver_phone);
                    setValue('country_code', order.receiver_country_code || "BR");
                }
            }
            setShowLoader(false);
        }

        fetchOrderData();
    }, [orderId, setValue]);

    // useEffect de busca por telefone removido por questões de LGPD
    // Os dados do cliente agora vêm diretamente da API getCompletedOrder

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            console.log(errors);
            let errorMessage = "";
    
            Object.values(errors).forEach((error, index) => {
                if (error?.message) {
                    errorMessage += index < 1 ? `${error.message}\n - ` : `${error.message}\n - `;
                }
            });

            setErrorMessage(errorMessage);

            setTimeout(() => {
                setErrorMessage("");
            }, 5000);
    
        } else {
            setErrorMessage("");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitCount]);

    const handlePickUpAddress = async (value: boolean) => {
        if (value) {
            const { data: pickUpAddress } = await getPickupAddress() as any;
            setAddressId(pickUpAddress.id);
            setValue("addressId", pickUpAddress.id);
            setValue("pickup_on_store", true);
        }

        if (!value) {
            setValue("addressId", "");
            setValue("pickup_on_store", false);
        }
    }

    const handleWriteMessageClick = () => {
        setValue("has_card", true);
        setShowRememberCardModal(false);
    
        setTimeout(() => {
            cardSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    };

    const handleNoMessageClick = () => {
        setShowRememberCardModal(false);
        handleSubmit(submitOrder)();
    };

    const handleCreateUser = async () => {
        const phoneNumber = watch('phone_number');
        const firstName = watch('first_name');
        const lastName = watch('last_name');

        if (!firstName || !lastName || !phoneNumber) {
            setError("phone_number", { message: "Preencha todos os campos" });
            setError("first_name", { message: "Preencha todos os campos" });
            setError("last_name", { message: "Preencha todos os campos" });
            return;
        }

        setError("phone_number", { message: "" });
        setError("first_name", { message: "" });
        setError("last_name", { message: "" });

        setShowLoader(true);

        let timeoutReached = false;

        const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => {
                timeoutReached = true;
                reject(new Error("Tempo de requisição excedido. Tente novamente."));
            }, 4000)
        );

        try {
            const clientPromise = createClientOnline({
                first_name: firstName,
                last_name: lastName,
                phone_number: rawTelephone(phoneNumber),
                country_code: watch("country_code") || "BR",
            });

            const result = await Promise.race([clientPromise, timeoutPromise]) as any;
            const clientData = result.data;

            if (!timeoutReached) {
                setClientId(clientData.id);
            }

        } catch (error: any) {
            console.error(error);
            
            // Verificar se é erro de cliente já cadastrado
            if (error.response?.data?.errorCode === 400 && 
                error.response?.data?.message === "Client already created") {
                setErrorMessage('Este número de telefone já está cadastrado no sistema. Para qualquer dúvida, entre em contato com a loja.');
                setTimeout(() => { setErrorMessage('') }, 4000);
            } else {
                setErrorMessage('Algo deu errado, por favor tente novamente');
                setTimeout(() => { setErrorMessage('') }, 1500);
            }
        } finally {
            setShowLoader(false);
        }
    };

    return (
        <Container>
            <WelcomeBackModal
                isOpen={showWelcomeModal}
                onRequestClose={() => setShowWelcomeModal(false)}
                name={`${watch("first_name")} ${watch("last_name")}`}
            />
            <RememberCardModal
                isOpen={showRememberCardModal}
                onRequestClose={() => setShowRememberCardModal(false)}
                handleWriteMessageClick={handleWriteMessageClick}
                handleNoMessageClick={handleNoMessageClick}
            />
            <TooltipModal
                isOpen={showToolTipModal}
                onRequestClose={() => setShowToolTipModal(false)}
                textContent={tooltipMessage}
                title=""
                showWhatsapp
                showCopyButton={false}
            />
            <Loader show={showLoader} />
            {errorMessage && <ErrorAlert message={errorMessage} />}
            {(!isWaitingForClienteOrder && !showLoader ) &&
                <CompletedOrder>
                    <img src={storeLogo} alt="" />
                    <h1>Seu pedido foi enviado para a loja!</h1>
                    {!currentOrder?.payment_received &&
                        <>
                            <h2>Para concluir, finalize o pagamento com nosso atendente no WhatsApp.</h2>
                            <h2>Assim que confirmado, daremos sequência ao seu pedido.</h2>
                            <p>🌺🌻🌸</p>
                        </>
                    }

                    {currentOrder?.payment_received &&
                        <h2>Qualquer dúvida entre em contato conosco.</h2>
                    }
                </CompletedOrder>
            }
            {isWaitingForClienteOrder &&
                <Form onSubmit={handleSubmit(submitOrder)}>
                    <FormHeader>
                        <img src={storeLogo} alt="" />
                        <p>{storeName}</p>
                        <span>CNPJ: {storeCNPJ}</span>
                        <span>Tel: {storePhone}</span>
                    </FormHeader>
                    <OrderReview>
                        <h1>Resumo do pedido</h1>
                        <div className="order-info-container">
                            <div className="description-container">
                                <p><strong>PEDIDO: </strong> #{currentOrder?.code}</p>
                                <p><strong>DESCRIÇÃO: </strong>
                                    {formatDescription(currentOrder?.description).map((line, idx) => (
                                        <p key={idx}>{line}</p>
                                    ))}
                                </p>
                            </div>
                            <div className="price-container">
                                <p><strong>Valor dos Produtos: </strong> {
                                    currentOrder?.products_value  &&
                                    convertMoney(currentOrder?.products_value as number)
                                }</p>
                                {(discount > 0) &&
                                    <p className="discount-value">
                                        <strong>Desconto
                                            {currentOrder?.coupon_code && (
                                                <span style={{ 
                                                    marginLeft: '0.5rem',
                                                    color: '#EC4899',
                                                    fontWeight: 600,
                                                    fontSize: '0.9em'
                                                }}>
                                                    (🎁 Cupom: {currentOrder.coupon_code})
                                                </span>
                                            )}
                                        </strong>
                                        {convertMoney(currentOrder?.discount as number)}
                                    </p>
                                }
                                <p><strong>Taxa de entrega: </strong> {
                                    currentOrder?.delivery_fee &&
                                    convertMoney(currentOrder?.delivery_fee as number)
                                }</p>
                                <p className="total-value"><strong>Valor Total: </strong> {
                                    currentOrder?.total &&
                                    convertMoney(currentOrder?.total as number)
                                }</p>
                            </div>
                            
                        </div>
                    </OrderReview>
                    <div className="spacer"></div>
                    <h1 className="client-container-title">Dados do Comprador</h1>
                    <FormFieldsContainer style={{ marginTop: "0px" }}>
                        <FormField>
                            <Label translate="no">
                                Seu telefone
                                <span>*</span>
                            </Label>
                            <PhoneInput
                                control={control}
                                setValue={setValue}
                                phoneFieldName="phone_number"
                                countryFieldName="country_code"
                                disabled
                            />
                            {errors.phone_number && <ErrorMessage>{errors.phone_number.message}</ErrorMessage>}
                        </FormField>
                        <InlineFormField>
                            <FormField>
                                <Label translate="no">
                                    Seu nome
                                    <span>*</span>
                                </Label>
                                <Input type="text" autoComplete="off" placeholder="Digite seu nome"
                                    {...register("first_name", {
                                        required: "Nome inválido",
                                    })}
                                />
                                {errors.first_name && <ErrorMessage>{errors.first_name.message}</ErrorMessage>}
                            </FormField>
                            <FormField>
                                <Label translate="no">
                                    Seu sobrenome
                                    <span>*</span>
                                </Label>
                                <Input translate="no" type="text" placeholder="Digite seu sobrenome" 
                                    {...register("last_name", {
                                        required: "Sobrenome inválido",
                                    })}
                                />
                                {errors.last_name && <ErrorMessage>{errors.last_name.message}</ErrorMessage>}
                            </FormField>
                        </InlineFormField>
                        {!client_id &&
                            <PrimaryButton
                                style={{width: "50%"}}
                                className="next-button"
                                type="button"
                                onClick={handleCreateUser}>
                                Continuar
                            </PrimaryButton>
                        }
                    </FormFieldsContainer>

                    {client_id &&
                        <>
                            <FormField>
                                <Label>
                                    Informe sobre a entrega
                                    <span>*</span>
                                </Label>
                                <Select {...register("type_of_delivery", { required: "Vendedor Responsável inválido" })}>
                                    <option value="">Escolher:</option>
                                    {Object.entries(TYPES_OF_DELIVERY).map(([key, value]) => (
                                        <option key={key} value={key}>{value}</option>
                                    ))}
                                </Select>
                                {errors.type_of_delivery && <ErrorMessage>{errors.type_of_delivery.message}</ErrorMessage>}
                            </FormField>
                            {typeOfDelivery === "SOMEONES_GIFT" &&
                                <InlineFormField>
                                    <FormField>
                                        <Label translate="no">
                                            Recebedor
                                            <span>*</span>
                                        </Label>
                                        <Input type="text" placeholder="Recebedor"
                                            {...register("receiver_name", {
                                                required: "Nome inválido",
                                            })}
                                        />
                                        {errors.receiver_name && <ErrorMessage>{errors.receiver_name.message}</ErrorMessage>}
                                    </FormField>
                                    <FormField>
                                        <Label translate="no">
                                            Telefone recebedor
                                            <span>*</span>
                                        </Label>
                                        <PhoneInput
                                            control={control}
                                            setValue={setValue}
                                            phoneFieldName="receiver_phone"
                                            countryFieldName="receiver_country_code"
                                            required
                                        />
                                        {errors.receiver_phone && <ErrorMessage>{errors.receiver_phone.message}</ErrorMessage>}
                                    </FormField>
                                </InlineFormField>
                            }
                            {addresses.length === 0 &&
                                <CheckboxContainer alignLeft>
                                    <Checkbox type="checkbox" onChange={(e) => {
                                        setPickupOnStore(!pickupOnStore)
                                        handlePickUpAddress(e.target.checked);
                                        
                                    }} checked={pickupOnStore}/>
                                    <Label>Retirar Pedido na loja</Label>
                                </CheckboxContainer>
                            }
                            {addresses.length > 0 && (
                                <>
                                    {!pickupOnStore &&
                                        <FormField>
                                            <Label>
                                                Selecione o Endereço (Ou cadastre um novo)
                                                <span>*</span>
                                            </Label>
                                            <Select
                                                {...register("addressId")}
                                                onChange={(e) => {
                                                    const selectedAddressId = e.target.value;
                                                    const selectedAddress: any = addresses.find((address: any) => address.id === selectedAddressId);
                                                    setSelectedAddress(selectedAddress);
                                                    if (selectedAddress) {
                                                        setValue("postal_code", selectedAddress.postal_code);
                                                        setValue("street", selectedAddress.street);
                                                        setValue("street_number", selectedAddress.street_number);
                                                        setValue("city", selectedAddress.city);
                                                        setValue("neighborhood", selectedAddress.neighborhood);
                                                        setValue("complement", selectedAddress.complement);
                                                        setValue("reference_point", selectedAddress.reference_point);
                                                        setValue("state", selectedAddress.state);
                                                        setValue("country", selectedAddress.country);
                                                        setAddressId(selectedAddressId);
                                                    }

                                                    if (!selectedAddress) {
                                                        setValue("postal_code", "");
                                                        setValue("street", "");
                                                        setValue("street_number", "");
                                                        setValue("city", "Itaperuna");
                                                        setValue("neighborhood", "");
                                                        setValue("complement", "");
                                                        setValue("reference_point", "");
                                                        setValue("state", "RJ");
                                                        setValue("country", "Brasil");
                                                        setAddressId("");
                                                    }
                                                }}
                                            >
                                                <option value="">Selecione um endereço</option>
                                                {addresses.map((address: any) => (
                                                    <option key={address.id} value={address.id}>
                                                        {address.street}, {address.number} - {address.city}
                                                    </option>
                                                ))}
                                            </Select>
                                        </FormField>
                                    }
                                    
                                    <InlineFormField>
                                        <CheckboxContainer>
                                            <Checkbox type="checkbox" disabled={pickupOnStore} onChange={() => {
                                                setNewAddress(!newAddress)

                                                if (!newAddress) {
                                                    setAddressId("");
                                                    setValue("addressId", "");
                                                }

                                                if (newAddress) {
                                                    setAddressId(selectedAddress.id);
                                                    setValue("addressId", selectedAddress.id);
                                                }
                                                
                                            }} checked={newAddress}/>
                                            <Label>Cadastrar novo endereço</Label>
                                        </CheckboxContainer>

                                        <CheckboxContainer>
                                            <Checkbox type="checkbox" onChange={(e) => {
                                                setPickupOnStore(!pickupOnStore)
                                                handlePickUpAddress(e.target.checked);
                                                
                                            }} checked={pickupOnStore}/>
                                            <Label>Retirar na Loja</Label>
                                        </CheckboxContainer>
                                    </InlineFormField>
                                </>
                            )}
                            {!pickupOnStore &&
                                <>
                                    <FormField>
                                        <Label>
                                            Rua
                                            <span>*</span>
                                        </Label>
                                        <Input type="text" placeholder="Rua" {...register("street", {
                                            required: "Rua inválida",
                                            })}
                                            disabled={(addresses.length > 0 && !newAddress) ? true : false}
                                        />
                                        {errors.street && <ErrorMessage>{errors.street.message}</ErrorMessage>}
                                    </FormField>
                                    <InlineFormField>
                                        <FormField isShortField>
                                            <Label>
                                                Número
                                                <span>*</span>
                                            </Label>
                                            <Input type="text" placeholder="Número" {...register("street_number", {
                                                required: "Número inválido",
                                                })}
                                                disabled={(addresses.length > 0 && !newAddress) ? true : false}
                                            />
                                            {errors.street_number && <ErrorMessage>{errors.street_number.message}</ErrorMessage>}
                                        </FormField>
                                        <FormField>
                                            <Label>
                                                Complemento
                                            </Label>
                                            <Input type="text" placeholder="Complemento" {...register("complement")}
                                                disabled={(addresses.length > 0 && !newAddress) ? true : false}
                                            />
                                        </FormField>
                                    </InlineFormField>
                                    <FormField>
                                        <Label>
                                            Bairro
                                            <span>*</span>
                                        </Label>
                                        <Input type="text" placeholder="Bairro" {...register("neighborhood", {
                                            required: "Bairro inválido",
                                            })}
                                            disabled={(addresses.length > 0 && !newAddress) ? true : false}
                                        />
                                        {errors.neighborhood && <ErrorMessage>{errors.neighborhood.message}</ErrorMessage>}
                                    </FormField>
                                    <FormField>
                                        <Label>Ponto de referência</Label>
                                        <Input type="text" placeholder="Ponto de referência" {...register("reference_point")}
                                            disabled={(addresses.length > 0 && !newAddress) ? true : false}
                                        />
                                    </FormField>

                                    <InlineFormField>
                                        <FormField>
                                            <Label>
                                                Estado
                                                <span>*</span>
                                            </Label>
                                            <Select disabled {...register("state", {
                                                required: "Estado inválido",
                                                })}>
                                                <option value="">Selecionar</option>
                                                {Object.entries(STATES).map(([key, value]) => (
                                                    <option key={key} value={key}>{value}</option>
                                                ))}
                                            </Select>
                                            {errors.state && <ErrorMessage>{errors.state.message}</ErrorMessage>}
                                        </FormField>
                                        <FormField>
                                            <Label style={{ display: "flex" }}>
                                                <div>
                                                    Cidade
                                                    <span>*</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="label-question"
                                                    onClick={() => setShowToolTipModal(!showToolTipModal)}>
                                                        <FontAwesomeIcon icon={faCircleQuestion}
                                                    />
                                                </button>
                                            </Label>
                                            <Input type="text" placeholder="Cidade" {...register("city", {
                                                required: "Cidade inválido",
                                                })}
                                                disabled
                                            />
                                            {errors.city && <ErrorMessage>{errors.city.message}</ErrorMessage>}
                                        </FormField>
                                    </InlineFormField>
                                </>
                            }
                            <FormField>
                                <Label>
                                    Data de Entrega
                                    <span>*</span>
                                </Label>
                                <Input 
                                    type="date" 
                                    min={moment().format('YYYY-MM-DD')}
                                    max={moment().add(maxDeliveryDaysAdvance, 'days').format('YYYY-MM-DD')}
                                    {...register("delivery_date", {
                                        required: "Data de entrega é obrigatória",
                                        validate: (value) => {
                                            const selectedDate = moment(value);
                                            const today = moment();
                                            const maxDate = moment().add(maxDeliveryDaysAdvance, 'days');
                                            
                                            // Verificar se a data é no passado
                                            if (selectedDate.isBefore(today, 'day')) {
                                                return "Não é possível selecionar uma data no passado.";
                                            }
                                            
                                            // Verificar se excede o limite de dias de antecedência
                                            if (selectedDate.isAfter(maxDate, 'day')) {
                                                return `Desculpe, só é possível agendar entregas até ${maxDeliveryDaysAdvance} dias de antecedência.`;
                                            }
                                            
                                            return true;
                                        }
                                    })}
                                />
                                {errors.delivery_date && <ErrorMessage>{errors.delivery_date.message}</ErrorMessage>}
                                <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px' }}>
                                    📅 Você pode agendar sua entrega com até {maxDeliveryDaysAdvance} dias de antecedência.
                                </p>
                            </FormField>
                            <FormField>
                                <CheckboxContainer>
                                    <Checkbox type="checkbox" {...register("has_card")} />
                                    <Label>Pedido Contém Cartão de Mensagem.</Label>
                                </CheckboxContainer>
                            </FormField>
                            {hasCard &&
                                <>
                                    <FormField ref={cardSectionRef}>
                                        <Label>
                                            De: <span>*</span>
                                        </Label>
                                        <Input {...register("card_from", {
                                            required: "Remetente do cartão é obrigatório",
                                        })}/>
                                        {errors.card_from && <ErrorMessage>{errors.card_from.message}</ErrorMessage>}
                                    </FormField>
                                    <FormField>
                                        <Label>
                                            Para: <span>*</span>
                                        </Label>
                                        <Input {...register("card_to", {
                                            required: "Destinatário do cartão é obrigatório",
                                        })}/>
                                        {errors.card_to && <ErrorMessage>{errors.card_to.message}</ErrorMessage>}
                                    </FormField>
                                    <FormField>
                                        <Label>Mensagem do cartão <span>*</span></Label>
                                        <Textarea {...register("card_message", {required: "Mensagem do cartão é obrigatório",})}
                                        />
                                        {errors.card_message && <ErrorMessage>{errors.card_message.message}</ErrorMessage>}
                                    </FormField>
                                </>
                            }
                            
                            <PrimaryButton type="submit">Finalizar Pedido</PrimaryButton>
                        </>
                    }
                </Form>
            }
            <CookieConsent />
        </Container>
    );
}
